import React, { useState } from 'react';
import { useObserver } from 'mobx-react';
import { useTheme } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import { Modal } from '@shared/components/Modal';
import { Button } from '@shared/components/Button';
import { FormError } from '@shared/components/FormError';
import { useDevicePairingUIStore, useUserPermissionsUIStore } from '@core/useStores';
import { APPROVED_DEVICE_OUTCOME, isCompletePairingCode } from 'AuthPart/DevicePairing/store';

import { CodeInput } from './CodeInput';
import { DeviceCard } from './DeviceCard';
import { PairForPicker } from './PairForPicker';
import { readableError } from './errors';
import { useStyles } from './PairDeviceModal.styles';
import { messages } from 'AuthPart/DevicePairing/devicePairing.messages';
import { ThemeProps } from '@styles/theme';

/**
 * Pairing a headset, as a dialog: enter the code, see what is asking, then say yes or no.
 * Lookup and approval are deliberately separate steps — a dialog that paired on submit would turn
 * a shoulder-surfed code into a silent account takeover.
 */
export const PairDeviceModal: React.FC = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const devicePairingUIStore = useDevicePairingUIStore();
    const userPermissionsUIStore = useUserPermissionsUIStore();
    const [draft, setDraft] = useState('');
    const [formatError, setFormatError] = useState(false);

    const close = () => {
        setDraft('');
        setFormatError(false);
        devicePairingUIStore.closeModal();
    };

    const restart = () => {
        setDraft('');
        setFormatError(false);
        devicePairingUIStore.reset();
    };

    const submit = (code: string) => {
        if (!isCompletePairingCode(code)) {
            setFormatError(true);

            return;
        }

        setFormatError(false);
        devicePairingUIStore.lookup(code);
    };

    return useObserver(() => {
        const { deviceInfo, outcome, lookupStatus, actionStatus, processError, modalOpened, pairFor } = devicePairingUIStore;

        // Managers pair devices for their trainees as much as administrators do, so the two are
        // one privilege here.
        const canPairForOthers = userPermissionsUIStore.isSuperAdmin ||
            userPermissionsUIStore.isMultiAdmin ||
            userPermissionsUIStore.isAdmin ||
            userPermissionsUIStore.isManager;

        const renderOutcome = () => {
            const approved = outcome === APPROVED_DEVICE_OUTCOME;

            const approvedBody = pairFor
                ? messages['devicePairing.approvedFor.body'](pairFor.fullName || pairFor.email)
                : messages['devicePairing.approved.body'];

            return (
                <>
                    <div className={styles.outcome}>
                        {
                            approved
                                ? <CheckCircleOutlineIcon className={`${styles.outcomeIcon} ${styles.outcomeApproved}`} />
                                : <HighlightOffIcon className={`${styles.outcomeIcon} ${styles.outcomeDenied}`} />
                        }

                        <h2 className={styles.title}>
                            {approved ? messages['devicePairing.approved.title'] : messages['devicePairing.denied.title']}
                        </h2>
                        <p className={styles.outcomeBody}>
                            {approved ? approvedBody : messages['devicePairing.denied.body']}
                        </p>
                    </div>

                    <div className={styles.buttons}>
                        <Button color="secondary" onClick={restart}>
                            {messages['devicePairing.action.pairAnother']}
                        </Button>

                        <Button onClick={close}>
                            {messages['devicePairing.action.done']}
                        </Button>
                    </div>
                </>
            );
        };

        const renderConfirm = () => {
            const pairingForSomeoneElse = !!pairFor;
            const targetName = pairFor?.fullName || pairFor?.email || '';

            return (
                <>
                    <div className={styles.header}>
                        <h2 className={styles.title}>
                            {
                                pairingForSomeoneElse
                                    ? messages['devicePairing.confirmFor.title']
                                    : messages['devicePairing.confirm.title']
                            }
                        </h2>
                        <p className={styles.subtitle}>
                            {
                                pairingForSomeoneElse
                                    ? messages['devicePairing.confirmFor.subtitle'](targetName)
                                    : messages['devicePairing.confirm.subtitle']
                            }
                        </p>
                    </div>

                    <DeviceCard
                        deviceName={deviceInfo!.deviceName}
                        devicePlatform={deviceInfo!.devicePlatform}
                        applicationVersion={deviceInfo!.applicationVersion}
                        requestIp={deviceInfo!.requestIp}
                        requestedAt={deviceInfo!.requestedAt}
                    />

                    {canPairForOthers && <PairForPicker />}

                    {
                        devicePairingUIStore.isPairTargetIncomplete && (
                            <div className={styles.errorWrapper}>
                                <FormError text={messages['devicePairing.pairFor.required']} />
                            </div>
                        )
                    }

                    {
                        actionStatus.isError && (
                            <div className={styles.errorWrapper}>
                                <FormError text={readableError(processError)} />
                            </div>
                        )
                    }

                    <p className={styles.warning}>
                        {
                            pairingForSomeoneElse
                                ? messages['devicePairing.confirmFor.warning']
                                : messages['devicePairing.confirm.warning']
                        }
                    </p>

                    <div className={styles.buttons}>
                        <Button
                            color="error"
                            onClick={() => devicePairingUIStore.deny()}
                            disabled={actionStatus.isLoading}
                        >
                            {messages['devicePairing.action.deny']}
                        </Button>

                        <Button
                            onClick={() => devicePairingUIStore.approve()}
                            loading={actionStatus.isLoading}
                            disabled={devicePairingUIStore.isPairTargetIncomplete}
                        >
                            {messages['devicePairing.action.approve']}
                        </Button>
                    </div>
                </>
            );
        };

        const renderEntry = () => (
            <>
                <div className={styles.header}>
                    <h2 className={styles.title}>{messages['devicePairing.title']}</h2>
                    <p className={styles.subtitle}>{messages['devicePairing.subtitle']}</p>
                </div>

                <CodeInput
                    value={draft}
                    disabled={lookupStatus.isLoading}
                    hasError={formatError || lookupStatus.isError}
                    clearLabel={messages['devicePairing.action.clear']}
                    onChange={value => {
                        setDraft(value);
                        setFormatError(false);
                        devicePairingUIStore.clearLookupError();
                    }}
                    onComplete={submit}
                />

                {
                    (formatError || lookupStatus.isError) && (
                        <div className={styles.errorWrapper}>
                            <FormError
                                text={formatError
                                    ? messages['devicePairing.error.format']
                                    : readableError(processError)}
                            />
                        </div>
                    )
                }

                <div className={styles.buttons}>
                    <Button color="secondary" onClick={close}>
                        {messages['devicePairing.action.cancel']}
                    </Button>

                    <Button
                        onClick={() => submit(draft)}
                        loading={lookupStatus.isLoading}
                        disabled={!isCompletePairingCode(draft)}
                    >
                        {messages['devicePairing.action.continue']}
                    </Button>
                </div>
            </>
        );

        return (
            <Modal
                phoneVariant="centered"
                isOpen={modalOpened}
                onRequestClose={close}
                className={styles.modal}
                shouldCloseOnOverlayClick
                shouldCloseOnEsc
            >
                {outcome ? renderOutcome() : deviceInfo ? renderConfirm() : renderEntry()}
            </Modal>
        );
    });
}
